import {
  UserPlus,
  Pencil,
  Trash2,
  Phone,
  MessageSquare,
  Mail,
  StickyNote,
  UserCheck,
  Briefcase,
  GraduationCap,
  Award,
  Building2,
  Users,
  FolderKanban,
  Wrench,
  FileText,
  Clock,
  LucideIcon,
} from 'lucide-react';
import { ActivityLog } from './types';

export interface ActivityConfig {
  icon: LucideIcon;
  color: string;
  bgColor: string;
  label: string;
  description: (log: ActivityLog) => string;
}

// Entity type configurations
const entityConfig: Record<string, { icon: LucideIcon; label: string }> = {
  candidates: { icon: Users, label: 'Candidate' },
  candidate_applications: { icon: FolderKanban, label: 'Application' },
  candidate_skills: { icon: Wrench, label: 'Skill' },
  candidate_education: { icon: GraduationCap, label: 'Education' },
  candidate_experience: { icon: Briefcase, label: 'Experience' },
  candidate_projects: { icon: FolderKanban, label: 'Project' },
  candidate_awards: { icon: Award, label: 'Award' },
  communications: { icon: MessageSquare, label: 'Communication' },
  jobs: { icon: Briefcase, label: 'Job' },
  job_stages: { icon: FolderKanban, label: 'Job Stage' },
  clients: { icon: Building2, label: 'Client' },
  companies: { icon: Building2, label: 'Company' },
  company_people: { icon: UserCheck, label: 'Contact' },
  skills: { icon: Wrench, label: 'Skill' },
};

// Action configurations
const actionConfig: Record<string, { icon: LucideIcon; color: string; bgColor: string; verb: string }> = {
  create: { 
    icon: UserPlus, 
    color: 'text-green-600 dark:text-green-400', 
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    verb: 'created'
  },
  update: { 
    icon: Pencil, 
    color: 'text-blue-600 dark:text-blue-400', 
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    verb: 'updated'
  },
  delete: { 
    icon: Trash2, 
    color: 'text-red-600 dark:text-red-400', 
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    verb: 'deleted'
  },
};

// Special action detection based on entity type and changes
export function detectSpecialAction(log: ActivityLog): string {
  const { entity_type, action, new_value, old_value } = log;

  // Communication-specific actions
  if (entity_type === 'communications' && action === 'create') {
    const channel = new_value?.channel;
    if (channel === 'phone') return 'call';
    if (channel === 'sms') return 'sms';
    if (channel === 'email') return 'email';
    if (channel === 'note') return 'note';
  }

  // Assignment detection
  if (entity_type === 'jobs' && action === 'update') {
    if (old_value?.assigned_to !== new_value?.assigned_to) {
      return 'assign';
    }
  }

  if (entity_type === 'candidate_applications' && action === 'update') {
    if (old_value?.stage_id !== new_value?.stage_id) {
      return 'stage_change';
    }
  }

  return action;
}

// Get icon and styling for a specific action
export function getActionConfig(action: string): { icon: LucideIcon; color: string; bgColor: string } {
  const specialActions: Record<string, { icon: LucideIcon; color: string; bgColor: string }> = {
    call: { 
      icon: Phone, 
      color: 'text-purple-600 dark:text-purple-400', 
      bgColor: 'bg-purple-100 dark:bg-purple-900/30'
    },
    sms: { 
      icon: MessageSquare, 
      color: 'text-teal-600 dark:text-teal-400', 
      bgColor: 'bg-teal-100 dark:bg-teal-900/30'
    },
    email: { 
      icon: Mail, 
      color: 'text-indigo-600 dark:text-indigo-400', 
      bgColor: 'bg-indigo-100 dark:bg-indigo-900/30'
    },
    note: { 
      icon: StickyNote, 
      color: 'text-yellow-600 dark:text-yellow-400', 
      bgColor: 'bg-yellow-100 dark:bg-yellow-900/30'
    },
    assign: { 
      icon: UserCheck, 
      color: 'text-orange-600 dark:text-orange-400', 
      bgColor: 'bg-orange-100 dark:bg-orange-900/30'
    },
    stage_change: { 
      icon: FolderKanban, 
      color: 'text-cyan-600 dark:text-cyan-400', 
      bgColor: 'bg-cyan-100 dark:bg-cyan-900/30'
    },
  };

  return specialActions[action] || actionConfig[action] || actionConfig.update;
}

// Get entity icon and label
export function getEntityConfig(entityType: string) {
  return entityConfig[entityType] || { icon: FileText, label: entityType.replace(/_/g, ' ') };
}

// Generate human-readable description
export function generateDescription(log: ActivityLog): string {
  const action = detectSpecialAction(log);
  const entity = getEntityConfig(log.entity_type);
  const actorName = log.app_users?.full_name || 'System';
  
  // Get entity name from the log data
  const entityName = getEntityName(log);

  switch (action) {
    case 'create':
      return `${actorName} created ${entity.label.toLowerCase()}${entityName ? ` "${entityName}"` : ''}`;
    case 'update':
      return `${actorName} updated ${entity.label.toLowerCase()}${entityName ? ` "${entityName}"` : ''}`;
    case 'delete':
      return `${actorName} deleted ${entity.label.toLowerCase()}${entityName ? ` "${entityName}"` : ''}`;
    case 'call':
      return `${actorName} made a phone call`;
    case 'sms':
      return `${actorName} sent an SMS`;
    case 'email':
      return `${actorName} sent an email`;
    case 'note':
      return `${actorName} added a note`;
    case 'assign':
      return `${actorName} changed assignment`;
    case 'stage_change':
      return `${actorName} moved candidate to a new stage`;
    default:
      return `${actorName} performed ${action} on ${entity.label.toLowerCase()}`;
  }
}

// Extract entity name from log data
export function getEntityName(log: ActivityLog): string | null {
  const data = log.new_value || log.old_value;
  if (!data) return null;

  // Try different name patterns
  if (data.first_name && data.last_name) {
    return `${data.first_name} ${data.last_name}`;
  }
  if (data.name) return data.name;
  if (data.title) return data.title;
  if (data.subject) return data.subject;
  if (data.institution_name) return data.institution_name;
  if (data.company_name) return data.company_name;

  return null;
}

// Get changed fields between old and new values
export function getChangedFields(log: ActivityLog): { field: string; oldVal: any; newVal: any }[] {
  if (!log.old_value || !log.new_value) return [];

  const changes: { field: string; oldVal: any; newVal: any }[] = [];
  const allKeys = new Set([
    ...Object.keys(log.old_value || {}),
    ...Object.keys(log.new_value || {})
  ]);

  // Fields to skip
  const skipFields = ['id', 'created_at', 'updated_at'];

  allKeys.forEach(key => {
    if (skipFields.includes(key)) return;
    
    const oldVal = log.old_value?.[key];
    const newVal = log.new_value?.[key];
    
    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      changes.push({ field: key, oldVal, newVal });
    }
  });

  return changes;
}

// Format field name for display
export function formatFieldName(field: string): string {
  return field
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

// Format value for display
export function formatValue(value: any): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ') || '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}
